import { useEffect, useRef, useState } from "react";
import { ThinkingIndicator } from "@/components/chat/ThinkingIndicator";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
}

export const ChatMessages = ({ messages, isLoading }: ChatMessagesProps) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // 🔹 Scroll only the chat window (not the entire page)
  useEffect(() => {
    if (autoScroll && chatContainerRef.current) {
      // Use requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTo({
            top: chatContainerRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
      });
    }
  }, [messages, isLoading, autoScroll]);

  // 🔹 Stop auto-scrolling if user scrolls up manually
  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container) return;

    // Use requestAnimationFrame to batch layout reads and avoid forced reflow
    requestAnimationFrame(() => {
      if (!container) return;
      const nearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;
      setAutoScroll(nearBottom);
    });
  };

  // 🔹 Empty state
  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <p>Ask me about my projects, blockchain, or experience at Coinbase.</p>
      </div>
    );
  }

  return (
    <div
      ref={chatContainerRef}
      onScroll={handleScroll}
      className="flex flex-col h-[375px] overflow-y-auto p-6 space-y-4 bg-background/60 rounded-lg border border-border"
    >
      {/* 🔹 Render messages */}
      {messages.map((msg, idx) => (
        <div
          key={idx}
          className={`flex ${
            msg.role === "user" ? "justify-end" : "justify-start"
          } animate-fade-in`}
        >
          <div
            className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
              msg.role === "user"
                ? "bg-primary text-primary-foreground"
                : "bg-card border border-border text-foreground"
            }`}
          >
            {msg.content}
          </div>
        </div>
      ))}

      {/* 🔹 Thinking indicator (like ChatGPT) */}
      {isLoading && (
        <div className="flex justify-start animate-fade-in">
          <div className="bg-card border border-border rounded-2xl px-4 py-3">
            <ThinkingIndicator />
          </div>
        </div>
      )}
    </div>
  );
};
